import { api, IInstrumentDetailsBE } from '@trader/api';
import { IMessage } from '@trader/types';
import { formatByPipSize } from '@trader/utils';
import { ILibrarySymbolInfo } from './types';
import {
  ErrorCallback,
  HistoryCallback,
  PeriodParams,
  ResolutionString,
  SubscribeBarsCallback,
} from './charting_library';
import { TInstrumentEntity, TTradingAccountModel } from '@trader/store';
import {
  getAccountTypeForConnection,
  productId,
  defaultPipSize,
  emptyFn,
  convertTimeToUtc,
} from '@trader/constants';
import { ITCStydy } from './utils/getTradingCentral';
import { HubConnection } from '@microsoft/signalr';

export const resolutionObject = {
  '1': 'Minute1',
  '5': 'Minute5',
  '15': 'Minute15',
  '30': 'Minute30',
  '60': 'Hour1',
  '120': 'Hour2',
  '240': 'Hour4',
  '1D': 'Day1',
  '1W': 'Week1',
  '1M': 'Month1',
};

export const invertedResolutionObject = {
  Minute1: '1',
  Minute5: '5',
  Minute15: '15',
  Minute30: '30',
  Hour1: '60',
  Hour2: '120',
  Hour4: '240',
  Day1: '1D',
  Week1: '1W',
  Month1: '1M',
};

// DatafeedConfiguration implementation
const configurationData = {
  // Represents the resolutions for bars supported by your datafeed
  supported_resolutions: Object.keys(
    resolutionObject
  ) as Array<ResolutionString>,
  // The `exchanges` arguments are used for the `searchSymbols` method if a user selects the exchange
  exchanges: [{ value: '' }],
  // The `symbols_types` arguments are used for the `searchSymbols` method if a user selects this symbol type
  symbols_types: [{ value: '' }],
};

let firstBar = 0;

export default (
  subscribe: (
    send: (connection: HubConnection | null) => Promise<void>,
    onSubscribeMessage?: (message: IMessage) => void
  ) => Promise<void>,
  getSymbolData: (symbol: string) => Promise<IInstrumentDetailsBE>,
  getIndicators: (symbol: string) => Promise<ITCStydy>,
  prefetchedData: Pick<
    TInstrumentEntity,
    'pipSize' | 'description' | 'category' | 'quotesHours'
  >,
  activeTradingAccount: TTradingAccountModel
) => ({
  onReady: (callback: (config: typeof configurationData) => void) => {
    setTimeout(() => callback(configurationData), 0);
  },
  searchSymbols: emptyFn,
  resolveSymbol: async (
    symbol: string,
    onSymbolResolvedCallback: (symbolInfo: ILibrarySymbolInfo) => void
  ) => {
    try {
      const resInfo = getSymbolData(symbol);
      const resIndicators = getIndicators(symbol);
      const [data, indicators] = await Promise.all([resInfo, resIndicators]);

      const getSession = (
        quotesHours?: IInstrumentDetailsBE['quotesHours']
      ): string => {
        if (!quotesHours) {
          return '24x7';
        }
        const isSunday = new Date().getDay() === 0;

        const sessions = quotesHours.map(time => {
          const from = time.from.replace(':', '');
          const to = time.to.replace(':', '');
          return from && to ? `${from}${isSunday ? 'F' : ''}-${to}` : '';
        });

        return sessions[0] ? sessions.join(',') : '24x7';
      };

      const symbolInfo: ILibrarySymbolInfo = {
        ticker: symbol as string,
        name: symbol as string,
        description: (data?.description as string) || '',
        pipSize: data?.pipSize || defaultPipSize,
        tradingCentral: indicators.prices || [],
        supported_resolutions: configurationData.supported_resolutions,
        type: data?.category || '',
        session: getSession(data?.quotesHours),
        timezone: 'Etc/UTC',
        exchange: '',
        minmov: 1,
        pricescale: 1000,
        has_intraday: true,
        visible_plots_set: 'ohlc',
        has_weekly_and_monthly: true,
        volume_precision: 2,
        data_status: 'streaming',
      };
      setTimeout(() => {
        onSymbolResolvedCallback(symbolInfo);
      }, 0);
    } finally {
      const sessions = prefetchedData?.quotesHours.length
        ? prefetchedData?.quotesHours.map(time => {
            const from = time.from.replace(':', '');
            const to = time.to.replace(':', '');
            return from && to ? `${from}-${to}` : '';
          })
        : [''];

      const symbolInfo: ILibrarySymbolInfo = {
        ticker: symbol as string,
        name: symbol as string,
        description: (prefetchedData?.description as string) || '',
        pipSize: prefetchedData?.pipSize || defaultPipSize,
        tradingCentral: [],
        supported_resolutions: configurationData.supported_resolutions,
        type: prefetchedData?.category || '',
        session: sessions[0] ? sessions.join(',') : '24x7',
        timezone: 'Etc/UTC',
        exchange: '',
        minmov: 1,
        pricescale: 1000,
        has_intraday: true,
        visible_plots_set: 'ohlc',
        has_weekly_and_monthly: true,
        volume_precision: 2,
        data_status: 'streaming',
      };
      setTimeout(() => {
        onSymbolResolvedCallback(symbolInfo);
      }, 0);
    }
  },

  getBars: async (
    symbolInfo: ILibrarySymbolInfo,
    resolution: ResolutionString,
    periodParams: PeriodParams,
    onHistoryCallback: HistoryCallback,
    onErrorCallback: ErrorCallback
  ) => {
    try {
      const response = await api.MarketData.getCandleBars(
        symbolInfo.name,
        resolutionObject[resolution],
        periodParams.firstDataRequest ? 0 : firstBar
      );

      if (response?.bars?.length === 0) {
        onHistoryCallback([], { noData: true });
        return;
      }

      // eslint-disable-next-line require-atomic-updates
      firstBar = response.bars[0].time.seconds;

      if (periodParams.firstDataRequest) {
        onHistoryCallback(
          response.bars.map(bar => ({
            time: bar.time.seconds * convertTimeToUtc,
            low: bar.low,
            high: bar.high,
            open: bar.open,
            close: bar.close,
            volume: bar.volume,
          })),
          { noData: false }
        );
      } else {
        // fetching more bars while scrolling
        onHistoryCallback(
          response.bars.map(bar => ({
            time: bar.time.seconds * convertTimeToUtc,
            low: bar.low,
            high: bar.high,
            open: bar.open,
            close: bar.close,
            volume: bar.volume,
          })),
          { noData: false }
        );
      }
    } catch (error) {
      onErrorCallback(error as string);
    }
  },

  subscribeBars: (
    symbolInfo: ILibrarySymbolInfo,
    _resolution: ResolutionString,
    onRealtimeCallback: SubscribeBarsCallback,
    _tick: string
  ) => {
    subscribe(
      async connection => {
        await connection?.send(
          'SubscribeToQuote',
          symbolInfo.name,
          1,
          productId[import.meta.env.VITE_PRODUCT],
          activeTradingAccount.platformLogin,
          getAccountTypeForConnection[activeTradingAccount.accountType]
        );
      },
      (message: IMessage) => {
        if (message.symbol === symbolInfo.name) {
          const price = formatByPipSize(message.ask, symbolInfo.pipSize);

          // const date = new Date(message.time);
          const currentDate = new Date();
          // const userTimezoneOffset = date.getTimezoneOffset() * timeFromUtc;
          const currentUserTimezoneOffset = currentDate.getTimezoneOffset();

          // const result = new Date(date.getTime() - userTimezoneOffset);
          const currentResult = new Date(
            currentDate.getTime() - currentUserTimezoneOffset
          );

          onRealtimeCallback({
            open: Number(price),
            high: Number(price),
            low: Number(price),
            close: Number(price),
            time: new Date(currentResult).getTime(),
          });
        }
      }
    );
    // async function start() {
    // if (connection.state !== HubConnectionState.Connected) {
    //   return;
    // }
    // try {
    // await connection.send(
    //   'SubscribeToQuote',
    //   symbolInfo.name,
    //   1,
    //   productId[import.meta.env.VITE_PRODUCT],
    //   activeTradingAccount.platformLogin,
    //   getAccountTypeForConnection[activeTradingAccount.accountType]
    // );
    // connection.on('OnQuote', (message: IMessage) => {
    //   if (message.symbol === symbolInfo.name) {
    //     const price = formatByPipSize(message.ask, symbolInfo.pipSize);
    //
    //     // const date = new Date(message.time);
    //     const currentDate = new Date();
    //     // const userTimezoneOffset = date.getTimezoneOffset() * timeFromUtc;
    //     const currentUserTimezoneOffset = currentDate.getTimezoneOffset();
    //
    //     // const result = new Date(date.getTime() - userTimezoneOffset);
    //     const currentResult = new Date(
    //       currentDate.getTime() - currentUserTimezoneOffset
    //     );
    //
    //     onRealtimeCallback({
    //       open: Number(price),
    //       high: Number(price),
    //       low: Number(price),
    //       close: Number(price),
    //       time: new Date(currentResult).getTime(),
    //     });
    //   }
    // });
    // } catch (err) {
    //   reconnect(start);
    // }
    // }

    // start();

    // connection.onclose(async () => {
    //   await start();
    // });
  },
  unsubscribeBars: emptyFn,
});
